import {Button} from '@chekinapp/ui';
import React, {useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useMutation} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useForm} from 'react-hook-form';
import ChekinProSettings from 'ChekinProSettings';
import {PATHS} from 'routes/constants';
import {
  FormTypes,
  OPTION_VALUES,
  SEARCH_RESERVATION_FORM_NAMES,
} from 'pages/SearchReservation/utils';
import {RESERVATION_ID_KEY, STATUSES} from 'utils/constants';
import {usePath} from 'hooks';
import {useVirtualKeyboard} from 'hooks/useVirtualKeyboard';
import {LocalStorage, useStatus, useTimeoutRef} from '@chekin/core';
import {cn, useModalControls} from '@chekin/guestapp-ui-kit';
import {useReservationNotFound} from '../../useReservationNotFound';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {StatusModalStates, useErrorModal} from '@chekin/shared';
import PageUniversalLink from 'components/PageUniversalLink';
import {ViewMainInfo} from '../../components/ViewMainInfo';
import {InputController as Input} from '@guestapp/old-ui-kit';
import {ReservationNotFoundModal} from 'components/ReservationNotFoundModal';
import {SearchingBookingModal} from '../../components/SearchingBookingModal';
import {createSession} from 'components/sessions/utils';
import {ArrowLeft} from 'lucide-react';
import {ContentButton, CustomBackButton, StyledFormFieldWrapper, Wrapper} from './styled';
import {API} from '@chekin/api';

function BookingCodeView() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {isLoading, setStatus} = useStatus();
  const {state} = useLocation();
  const {ErrorModal, displayError} = useErrorModal();
  const timeoutRef = useTimeoutRef();
  const [modalRegisterGuestStatus, setModalRegisterGuestStatus] = React.useState(
    StatusModalStates.IDLE,
  );
  const layoutMode = useLayoutMode();

  const {
    isOpen: isOpenReservationStatusModal,
    openModal: openReservationStatusModal,
    closeModal: closeReservationStatusModal,
  } = useModalControls();

  const {canCreate, isNotFoundModalOpen, openNotFoundModal, closeNotFoundModal} =
    useReservationNotFound();

  const {
    register,
    control,
    handleSubmit,
    trigger,
    formState: {errors, isSubmitted},
    watch,
    getValues,
    setValue,
  } = useForm<FormTypes>();
  const bookingReference = watch(SEARCH_RESERVATION_FORM_NAMES.booking_reference);

  const {mutate} = useMutation({
    mutationFn: (formData: FormTypes) => {
      const booking_reference = formData[SEARCH_RESERVATION_FORM_NAMES.booking_reference];

      const params = {
        booking_reference: booking_reference,
        housing_id: state?.housingId,
      };

      return API.reservation.findReservation(params);
    },
    onSuccess: data => {
      if (data) {
        const reservationId = data?.results?.[0]?.id;
        const reservationSignUpFormLink = data?.results?.[0]?.signup_form_link;
        if (reservationId) {
          if (ChekinProSettings.isSDK) {
            setStatus(STATUSES.success);
            setModalRegisterGuestStatus(StatusModalStates.SUCCESS);
            ChekinProSettings.events.onReservationFoundFromHousing({id: reservationId});
            return;
          }

          createSession({reservationId});

          LocalStorage.set(RESERVATION_ID_KEY, reservationId);
          window.location.href = reservationSignUpFormLink;
          setModalRegisterGuestStatus(StatusModalStates.SUCCESS);
        } else {
          setModalRegisterGuestStatus(StatusModalStates.IDLE);
          closeReservationStatusModal();
          setStatus(STATUSES.idle);

          canCreate ? goToNewReservation() : openNotFoundModal();
        }
      }
    },
    onError: error => {
      setStatus(STATUSES.error);
      closeReservationStatusModal();
      displayError(error);
    },
  });

  const onSubmit = (formData: FormTypes) => {
    setStatus(STATUSES.loading);
    setModalRegisterGuestStatus(StatusModalStates.LOADING);
    openReservationStatusModal();
    mutate(formData);
  };

  const goToNewReservation = () => {
    navigate(getFullPath(PATHS.searchReservation.newReservation), {
      replace: true,
      state: {
        housingId: state?.housingId,
        country: state?.housingCountry,
        from: OPTION_VALUES.booking_reference,
        mode: state?.mode,
      },
    });
  };

  React.useEffect(
    function changeTranslationErrorMessages() {
      if (isSubmitted) {
        trigger();
      }
    },
    [isSubmitted, trigger],
  );

  React.useEffect(() => {
    if (
      !ChekinProSettings.isSDK &&
      modalRegisterGuestStatus === StatusModalStates.SUCCESS
    ) {
      timeoutRef.current = setTimeout(() => {
        closeReservationStatusModal();
      }, 3000);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [closeReservationStatusModal, modalRegisterGuestStatus, timeoutRef]);

  const virtualKeyboardConfig = useMemo(
    () => ({
      fields: {
        [SEARCH_RESERVATION_FORM_NAMES.booking_reference]: {
          getValue: () =>
            getValues(SEARCH_RESERVATION_FORM_NAMES.booking_reference) || '',
          setValue: (value: string) => {
            setValue(SEARCH_RESERVATION_FORM_NAMES.booking_reference, value);
          },
        },
      },
    }),
    [getValues, setValue],
  );

  const {handleFocus, handleBlur, keyboard} = useVirtualKeyboard(virtualKeyboardConfig);

  const handleBackClick = () => {
    navigate(getFullPath(PATHS.searchReservation.main), {
      state: {mode: OPTION_VALUES.booking_reference},
    });
  };

  return (
    <PageUniversalLink className="booking-code-view">
      <Wrapper className="kiosko:mt-[360px] kiosko:gap-10">
        <ViewMainInfo
          title={t('find_your_booking')}
          subTitle={t('enter_the_reference_code_assigned_to_your_booking')}
        />

        {!state?.mode && layoutMode === 'default' && (
          <CustomBackButton
            onClick={handleBackClick}
            className="summary-view__back-btn !w-auto"
            variant="link"
          >
            <ArrowLeft size={18} />
            {t('back')}
          </CustomBackButton>
        )}

        <StyledFormFieldWrapper className="kiosko:w-full">
          <Input
            {...register(SEARCH_RESERVATION_FORM_NAMES.booking_reference, {
              required: t('required'),
            })}
            label={t('booking_reference_code')}
            control={control}
            error={errors[SEARCH_RESERVATION_FORM_NAMES.booking_reference]?.message}
            autoCorrect="off"
            spellCheck={false}
            onFocus={() => {
              handleFocus(SEARCH_RESERVATION_FORM_NAMES.booking_reference);
            }}
            onBlur={() => {
              handleBlur(SEARCH_RESERVATION_FORM_NAMES.booking_reference);
            }}
          />
        </StyledFormFieldWrapper>

        <ContentButton className="kiosko:flex kiosko:flex-col kiosko:gap-8">
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading || !bookingReference}
            className={cn(
              'submit-btn',
              'min-w-[205px]',
              'kiosko:mx-auto kiosko:mt-4 kiosko:block',
            )}
            variant="default"
          >
            {t('confirm')}
          </Button>
          {layoutMode === 'kiosko' && (
            <Button
              onClick={handleBackClick}
              disabled={isLoading}
              className={cn(
                'submit-btn',
                'min-w-[205px]',
                'kiosko:mx-auto kiosko:mt-4 kiosko:block',
              )}
              variant="secondary"
            >
              {t('back')}
            </Button>
          )}
        </ContentButton>
      </Wrapper>
      <SearchingBookingModal
        isOpen={isOpenReservationStatusModal}
        status={modalRegisterGuestStatus}
      />
      <ErrorModal />

      <ReservationNotFoundModal open={isNotFoundModalOpen} onClose={closeNotFoundModal} />
      {keyboard}
    </PageUniversalLink>
  );
}

export {BookingCodeView};
